import { useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import type { EmailTemplateDto, UpdateEmailTemplateRequest } from '@ecom/contracts';
import { useAuthStore } from '@/stores/auth.store';
import { isUnchanged, useEmailTemplatesStore } from '@/stores/email-templates.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { SETTINGS_PAGES } from '@/lib/settings-pages';
import { ToggleField } from '@/components/fields';
import { MailIcon } from '@/components/icons';
import { Section, Tabs } from '@/components/page';
import { Alert, Button, Card, EmptyState, Field, Input, Pill, Skeleton, cx } from '@/components/ui';
import { SettingsPage } from '@/pages/settings';
import { SchemaSections } from '@/pages/settings/schema';

type NotificationsTab = 'templates' | 'sender';

/**
 * Email templates.
 *
 * A store owner writes what their customers read. The catalog is fixed — these
 * are the moments the platform speaks — but every word is theirs, in both
 * languages, with the placeholders it may use listed beside the box rather than
 * documented somewhere else.
 *
 * Same sub-menu shape as Settings: the list on one side, one template open on
 * the other. An owner arrives looking for a specific message, not for a scroll.
 * Who the mail comes from is the second tab, beside the templates.
 */
export function EmailTemplatesPage() {
  const { templates, loading, error, load } = useEmailTemplatesStore();
  const user = useAuthStore((state) => state.user);
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [tab, setTab] = useState<NotificationsTab>('templates');
  // `?key=` opens one template — the automations screen links each of its mails here (ADR-099).
  const [params] = useSearchParams();
  const [selected, setSelected] = useState<string>(params.get('key') ?? '');

  useEffect(() => {
    void load();
  }, [load]);

  // The API enforces this; hiding the form is a courtesy, not the boundary.
  if (user?.role !== 'OWNER') {
    return (
      <SettingsPage title="settingsNotifications" subtitle="settingsNotificationsHint">
        <Card className="flex flex-1 flex-col">
          <EmptyState title={t('ownerOnlySettings')} icon={<MailIcon />} />
        </Card>
      </SettingsPage>
    );
  }

  const active = templates.find((template) => template.key === selected) ?? templates[0];

  return (
    <SettingsPage
      title="settingsNotifications"
      subtitle="settingsNotificationsHint"
      tabs={
        <Tabs
          label={t('settingsNotifications')}
          value={tab}
          onChange={setTab}
          items={[
            { id: 'templates', label: t('notificationTemplates') },
            { id: 'sender', label: t('notificationSender') },
          ]}
        />
      }
    >
      {tab === 'sender' ? (
        <SchemaSections keys={SETTINGS_PAGES.notifications} />
      ) : loading ? (
        <Skeleton className="h-96" />
      ) : (
        <>
          <p className="text-sm text-ink-muted">{t('emailTemplatesSubtitle')}</p>

          {error ? <Alert>{error}</Alert> : null}

          <div className="grid items-start gap-5 lg:grid-cols-[16rem_minmax(0,1fr)]">
            <Card className="overflow-hidden lg:sticky lg:top-5">
              <nav aria-label={t('emailTemplates')}>
                <ul className="divide-y divide-line">
                  {templates.map((template) => (
                    <li key={template.key}>
                      <button
                        type="button"
                        onClick={() => setSelected(template.key)}
                        aria-current={template.key === active?.key}
                        className={cx(
                          'flex w-full items-center gap-3 px-4 py-3 text-start transition-colors',
                          template.key === active?.key ? 'bg-brand-50' : 'hover:bg-surface-sunken',
                        )}
                      >
                        <span className="min-w-0 flex-1">
                          <span
                            className={cx(
                              'block truncate text-sm',
                              template.key === active?.key ? 'font-medium text-brand-700' : 'text-ink',
                            )}
                          >
                            {template.name[locale]}
                          </span>
                          <span className="block truncate text-[11px] text-ink-faint">{template.when[locale]}</span>
                        </span>

                        {/* Two states worth seeing before opening: silenced, and edited. */}
                        {!template.isEnabled ? (
                          <Pill tone="neutral">{t('emailOff')}</Pill>
                        ) : template.isCustomised ? (
                          <Pill tone="info">{t('emailEdited')}</Pill>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </Card>

            <div className="min-w-0">{active ? <TemplateEditor key={active.key} template={active} /> : null}</div>
          </div>
        </>
      )}
    </SettingsPage>
  );
}

function TemplateEditor({ template }: { template: EmailTemplateDto }) {
  const { saving, savedKey, save, reset, preview, previewLoading, loadPreview, clearPreview, sendTest, testResult } =
    useEmailTemplatesStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const [draft, setDraft] = useState<UpdateEmailTemplateRequest>(toDraft(template));
  const [testTo, setTestTo] = useState('');

  // A save or a reset returns the stored template; the form follows it rather
  // than keeping an edit the server did not accept.
  useEffect(() => {
    setDraft(toDraft(template));
  }, [template]);

  /**
   * Where a placeholder chip inserts.
   *
   * Tracked per field so "insert {{order.number}}" lands where the cursor was,
   * not at the end of whichever box was touched last.
   */
  const focused = useRef<{ field: keyof UpdateEmailTemplateRequest; start: number; end: number } | null>(null);

  const insert = (token: string) => {
    const target = focused.current;
    if (!target) return;

    const field = target.field as 'subjectAr' | 'subjectEn' | 'bodyAr' | 'bodyEn';
    const current = String(draft[field] ?? '');
    const next = `${current.slice(0, target.start)}{{${token}}}${current.slice(target.end)}`;

    setDraft((state) => ({ ...state, [field]: next }));
  };

  const track =
    (field: 'subjectAr' | 'subjectEn' | 'bodyAr' | 'bodyEn') =>
    (event: { currentTarget: { selectionStart: number | null; selectionEnd: number | null } }) => {
      focused.current = {
        field,
        start: event.currentTarget.selectionStart ?? 0,
        end: event.currentTarget.selectionEnd ?? 0,
      };
    };

  const unchanged = isUnchanged(template, draft);

  return (
    <div className="flex flex-col gap-5">
      <Section
        title={template.name[locale]}
        subtitle={template.when[locale]}
        action={
          template.isCustomised ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={saving}
              onClick={() => {
                clearPreview();
                void reset(template.key);
              }}
            >
              {t('restoreDefault')}
            </Button>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-4">
          <ToggleField
            label={t('emailEnabled')}
            hint={t('emailEnabledHint')}
            checked={draft.isEnabled ?? true}
            onChange={(next) => setDraft((state) => ({ ...state, isEnabled: next }))}
          />

          <div className="grid gap-4 md:grid-cols-2">
            <Field label={`${t('subject')} — ${t('arabic')}`} htmlFor={`${template.key}-subject-ar`}>
              <Input
                id={`${template.key}-subject-ar`}
                dir="rtl"
                value={draft.subjectAr}
                onSelect={track('subjectAr')}
                onFocus={track('subjectAr')}
                onChange={(event) => setDraft((state) => ({ ...state, subjectAr: event.target.value }))}
              />
            </Field>

            <Field label={`${t('subject')} — ${t('english')}`} htmlFor={`${template.key}-subject-en`}>
              <Input
                id={`${template.key}-subject-en`}
                dir="ltr"
                value={draft.subjectEn}
                onSelect={track('subjectEn')}
                onFocus={track('subjectEn')}
                onChange={(event) => setDraft((state) => ({ ...state, subjectEn: event.target.value }))}
              />
            </Field>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <BodyBox
              id={`${template.key}-body-ar`}
              label={`${t('emailBody')} — ${t('arabic')}`}
              value={draft.bodyAr}
              onTrack={track('bodyAr')}
              onChange={(value) => setDraft((state) => ({ ...state, bodyAr: value }))}
            />
            <BodyBox
              id={`${template.key}-body-en`}
              label={`${t('emailBody')} — ${t('english')}`}
              value={draft.bodyEn}
              onTrack={track('bodyEn')}
              onChange={(value) => setDraft((state) => ({ ...state, bodyEn: value }))}
            />
          </div>

          <div>
            <p className="mb-2 text-xs font-medium text-ink-muted">{t('placeholders')}</p>
            <p className="mb-2 text-[11px] text-ink-faint">{t('placeholdersHint')}</p>
            <div className="flex flex-wrap gap-1.5">
              {template.placeholders.map((placeholder) => (
                <button
                  key={placeholder.key}
                  type="button"
                  // Mousedown, not click: clicking would blur the box first and
                  // lose the cursor position the insert depends on.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    insert(placeholder.key);
                  }}
                  title={placeholder.key}
                  className="rounded-full border border-line bg-surface-sunken px-2.5 py-1 text-[11px] text-ink-muted transition-colors hover:border-brand-600 hover:text-brand-700"
                >
                  {placeholder.label[locale]}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 border-t border-line pt-4">
            <Button
              disabled={saving || unchanged}
              onClick={async () => {
                if (await save(template.key, draft)) clearPreview();
              }}
            >
              {saving ? t('saving') : t('save')}
            </Button>

            <Button
              variant="secondary"
              disabled={previewLoading}
              onClick={() => void loadPreview(template.key)}
            >
              {previewLoading ? t('loading') : t('emailPreview')}
            </Button>

            {savedKey === template.key && unchanged ? (
              <span className="text-sm text-success">{t('saved')}</span>
            ) : null}
          </div>
        </div>
      </Section>

      {preview ? (
        <Section title={t('emailPreview')} subtitle={t('emailPreviewHint')}>
          <div>
            <p className="mb-3 text-sm font-medium text-ink">{preview.subject}</p>
            {/*
              An iframe, not dangerouslySetInnerHTML: the preview must render
              with the mail's own styling rather than the admin's, and a
              sandboxed frame keeps whatever is in the template from touching
              this page.
            */}
            <iframe
              title={t('emailPreview')}
              sandbox=""
              srcDoc={`<!doctype html><meta charset="utf-8"><body style="margin:0;padding:16px;background:#fff">${preview.html}</body>`}
              className="h-96 w-full rounded-control border border-line bg-white"
            />
          </div>
        </Section>
      ) : null}

      <Section title={t('sendTest')} subtitle={t('sendTestHint')}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1">
            <Field label={t('email')} htmlFor={`${template.key}-test-to`}>
              <Input
                id={`${template.key}-test-to`}
                type="email"
                dir="ltr"
                value={testTo}
                placeholder="owner@example.com"
                onChange={(event) => setTestTo(event.target.value)}
              />
            </Field>
          </div>
          <Button
            variant="secondary"
            disabled={!testTo}
            onClick={() => void sendTest(template.key, testTo)}
          >
            {t('send')}
          </Button>

          {testResult ? (
            <span className="text-sm text-success" dir="ltr">
              {t('testSent')} {testResult.to} ({testResult.driver})
            </span>
          ) : null}
        </div>
      </Section>
    </div>
  );
}

/**
 * The body editor.
 *
 * Always left-to-right, even for the Arabic body: what is being edited is
 * markup, and RTL turns `</p>` into `p>/<` on screen. The Arabic prose inside
 * still shapes and aligns correctly — bidi handles the runs, and the tags stay
 * readable. What the customer sees is RTL; this box is the source, not the mail.
 */
function BodyBox({
  id,
  label,
  value,
  onChange,
  onTrack,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onTrack: (event: { currentTarget: { selectionStart: number | null; selectionEnd: number | null } }) => void;
}) {
  return (
    <Field label={label} htmlFor={id}>
      <textarea
        id={id}
        dir="ltr"
        rows={9}
        value={value}
        onSelect={onTrack}
        onFocus={onTrack}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-control border border-line bg-surface px-3 py-2 font-mono text-xs leading-relaxed text-ink outline-none transition-colors focus:border-brand-600"
      />
    </Field>
  );
}

function toDraft(template: EmailTemplateDto): UpdateEmailTemplateRequest {
  return {
    subjectAr: template.subjectAr,
    subjectEn: template.subjectEn,
    bodyAr: template.bodyAr,
    bodyEn: template.bodyEn,
    isEnabled: template.isEnabled,
  };
}
